// useComprehendLive.js
// React glue: owns the AgentRuntime lifecycle, the Pulse (ambient trigger
// loop), wake-word arming, and the LiveStore; exposes a single hook the
// ComprehendLive components consume.
//
// Ambient principles (docs/comprehend-live-pulse-and-demo.md):
// - wake word arms and the realtime session pre-warms when the panel mounts
// - every executed mutating tool posts a receipt into the chat ledger
// - opening a patient drill-in enriches the agent's standing context
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';
import { AgentRuntime } from './AgentRuntime';
import {
  liveStore,
  agentHoldsAudio,
  persistVoiceRepliesPref,
  persistLocal,
  SETTINGS_KEYS,
} from './liveStore';
import { focusTracker } from './focus';
import { Pulse } from './pulse';
import {
  armWakeWord,
  disarmWakeWord,
  playBlipUp,
  playBlipDown,
} from './wakeWord';
import {
  createCoreTools,
  applyQueueItemFix,
  undoQueueItemFix,
  dismissQueueItemAnimated,
  receiptForToolCall,
  stepForToolCall,
} from './tools/index';
import { isDemoMode, setupDemo, DEMO_FLAG_KEY } from './demo/index';
import { AmbientRuntime } from './ambient';
import { getSyncCalContext, runScheduleSync } from '../api/scheduleSync';
import { computeActiveSet } from './multiRecord';

const HYDRATE_INTERVAL_MS = 60 * 1000;
const CONTEXT_PUSH_DEBOUNCE_MS = 1500;
const PREWARM_DELAY_MS = 2500;
const WAKE_LISTEN_TIMEOUT_MS = 15 * 1000; // clinic mode
const CONVERSATION_LISTEN_TIMEOUT_MS = 60 * 1000; // conversation mode

// Mic-state chirps. The blip-UP (wake heard) plays in ALL modes: it's an
// INPUT acknowledgment for an inherently eyes-free moment — you say "Hey
// Comprehend" looking at the patient, before you've even decided on a voice
// vs text answer, and the screen aura is only in peripheral vision then.
// The blip-DOWN (stopped listening) stays gated to voice mode: a closing
// chime after a silent text reply feels like it's announcing something that
// isn't audible, and the aura fading already signals the turn ended.
//
// A one-word acknowledgment ("Done.", "Got it") adds nothing over the
// receipt that every action already posts — so in text mode we don't render
// it as a chat bubble at all (it would also become the ghost trace, hiding
// the more useful receipt). In voice mode the spoken ack is legitimate, so
// we leave it. Matches the directive: text commands need no narration.
const ACK_PHRASES = new Set([
  'done',
  'got it',
  'ok',
  'okay',
  'sure',
  'on it',
  'all set',
  'you got it',
  'will do',
]);
const isTrivialAck = (text) =>
  ACK_PHRASES.has(
    (text || '')
      .toLowerCase()
      .replace(/[.!…\s]+$/g, '')
      .trim(),
  );

const blipDown = () => {
  if (liveStore.getState().voiceReplies) playBlipDown();
};

/** May we open the mic right now? Three independent switches can veto it:
 * autoListen (persisted opt-in), micKilled (hard panic), micMuted (soft
 * pause). EVERY arm / re-arm path reads this one predicate, so un-killing or
 * un-muting can't resurrect the wake word while listening is switched off. */
const canArmMic = () => {
  const s = liveStore.getState();
  return s.autoListen && !s.micKilled && !s.micMuted;
};

const proposalTimeToMs = (time, baseDate = new Date()) => {
  const raw = String(time || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\./g, '');
  if (!raw) return null;
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?(am|pm|a|p)?$/);
  if (!match) return null;

  let hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  const meridiem = match[3];
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute > 59) {
    return null;
  }
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (hour === 12) hour = 0;
    if (meridiem.startsWith('p')) hour += 12;
  } else if (hour > 23) {
    return null;
  }

  const date = new Date(baseDate);
  date.setHours(hour, minute, 0, 0);
  return date.getTime();
};

const ComprehendLiveContext = createContext(null);

export const ComprehendLiveProvider = ({ children }) => {
  const { apiClient: realApiClient } = useContext(GlobalContext);
  const navigate = useNavigate();

  // Demo mode wraps the api client: real ephemeral keys (live voice),
  // canned data. Decided 6/9: "live voice, scripted data".
  const demoRef = useRef(null);
  if (demoRef.current === null) {
    demoRef.current = isDemoMode() ? setupDemo(realApiClient) : false;
  }
  const apiClient = demoRef.current ? demoRef.current.apiClient : realApiClient;

  const [state, setState] = useState(liveStore.getState());
  const runtimeRef = useRef(null);
  const pulseRef = useRef(null);
  const assistantMsgRef = useRef(new Map()); // responseId -> chat message id
  const speechMsgRef = useRef(new Map()); // audio itemId -> placeholder msg id
  const contextTimerRef = useRef(null);
  const wakeListenTimerRef = useRef(null);
  const wakeInitiatedRef = useRef(false);
  const catchupTimerRef = useRef(null);

  // Mirror store -> React state
  useEffect(() => liveStore.subscribe(setState), []);

  // Resolve the tray mode from feature flags. comprehend_live wins -> ambient;
  // else multirecord_tray -> multirecord; else default to ambient. Flags live
  // on the REAL client (the demo wrapper doesn't proxy `features`), and we
  // honor them even in demo mode — so flipping comprehend_live off / leaving
  // multirecord_tray on shows MultiRecord++ with the demo's canned schedule,
  // instead of always forcing the agent. Re-checked when the profile loads.
  useEffect(() => {
    const resolve = () => {
      const f = realApiClient?.features || {};
      liveStore.setState({
        trayMode: f.comprehend_live
          ? 'ambient'
          : f.multirecord_tray
            ? 'multirecord'
            : 'ambient',
      });
    };
    resolve();
    if (realApiClient.onProfileUpdated) {
      realApiClient.onProfileUpdated(resolve);
      return () => realApiClient.offProfileUpdated?.(resolve);
    }
    return undefined;
  }, [apiClient, realApiClient]);

  // Feed the scripted demo data once on mount, in EITHER tray mode. The
  // ambient arming effect (which skips multirecord) used to own this call, so
  // demo never started on the timeline — setDemo never fired and the canned
  // schedule never loaded, making the DEMO toggle look like a no-op.
  const demoStartedRef = useRef(false);
  useEffect(() => {
    if (demoRef.current && !demoStartedRef.current) {
      demoStartedRef.current = true;
      demoRef.current.start({ pulse: pulseRef.current });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate once on mount (so the collapsed rail has data), then poll ONLY
  // while the panel is open. Collapsed, there's nothing to refresh — the rail's
  // recording state is driven live by the recorder runtime, not by polling —
  // so a 60s poll of the (wide) conversations window while collapsed is pure
  // waste. Re-hydrates immediately on expand. (No-op in demo mode.)
  useEffect(() => {
    liveStore.hydrate(apiClient);
    if (!state.expanded) return undefined;
    const interval = setInterval(
      () => liveStore.hydrate(apiClient),
      HYDRATE_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, [apiClient, state.expanded]);

  // Debounced store snapshot -> agent context, and the recording rule:
  // when recording STARTS, voice replies auto-mute once (receipt explains);
  // the toggle stays functional so the clinician can opt back in.
  const prevAnyRecordingRef = useRef(false);
  const prevOverlayRef = useRef(false);
  useEffect(() => {
    const unsubscribe = liveStore.subscribe((s) => {
      // (Demo mode keeps voice on — its scripted beats are spoken while
      // the compressed schedule records nearly continuously.)
      if (
        s.anyRecording &&
        !prevAnyRecordingRef.current &&
        s.voiceReplies &&
        !s.demo
      ) {
        liveStore.setState({ voiceReplies: false });
        persistVoiceRepliesPref(false);
        runtimeRef.current?.setVoiceRepliesPreferred(false);
        liveStore.addReceipt({
          text: 'Voice replies muted — visit recording',
          why: 'Tap the speaker icon to turn voice back on',
        });
      }
      prevAnyRecordingRef.current = s.anyRecording;
      // Conversation layer dismissed -> close the mic (conversation mode
      // keeps it open while the layer is up).
      if (prevOverlayRef.current && !s.chatOverlay) {
        wakeInitiatedRef.current = false;
        clearTimeout(wakeListenTimerRef.current);
        runtimeRef.current?.stopListening();
      }
      prevOverlayRef.current = s.chatOverlay;
      // Stand the ambient brain down while the realtime agent actually owns the
      // audio, so it doesn't double-act on the words "Hey Comprehend" is
      // handling (or transcribe the agent's own reply). Keyed on
      // agentHoldsAudio, NOT on chatOverlay: the overlay lingers 10-20s after
      // the agent goes quiet to let you read the reply, and gating on it left
      // ambient deaf for that whole tail with no on-screen cue.
      // Runs before the runtime guard below — ambient can be up without it.
      ambientRef.current?.setSuppressed(agentHoldsAudio(s));
      const runtime = runtimeRef.current;
      if (!runtime) return;
      runtime.setRecordingActive(s.anyRecording);
      clearTimeout(contextTimerRef.current);
      contextTimerRef.current = setTimeout(() => {
        runtime.addContext(
          'comprehend_live_panel',
          liveStore.getAgentContextSnapshot(),
        );
      }, CONTEXT_PUSH_DEBOUNCE_MS);
    });
    return () => {
      unsubscribe();
      clearTimeout(contextTimerRef.current);
    };
  }, []);

  const ensureRuntime = useCallback(async () => {
    if (runtimeRef.current) {
      if (
        runtimeRef.current.status === 'idle' ||
        runtimeRef.current.status === 'error'
      ) {
        await runtimeRef.current.init();
      }
      return runtimeRef.current;
    }
    const runtime = new AgentRuntime(apiClient, {
      voiceReplies: liveStore.getState().voiceReplies,
      verbosity: liveStore.getState().agentVerbosity,
      deviceId: liveStore.getState().micId || null,
    });
    runtimeRef.current = runtime;

    runtime.on('status', (status) =>
      liveStore.setState({ agentStatus: status }),
    );
    runtime.on('speaking_change', (speaking) =>
      liveStore.setState({ agentSpeaking: speaking }),
    );
    runtime.on('assistant_delta', ({ responseId, text }) => {
      // Text mode: don't stream a bubble — we can't tell yet if this turn is
      // just a trivial ack, and a "Done." flash then removal is jank. Hold
      // the typing indicator (awaitingReply) and decide once at assistant_done.
      // Voice mode streams as before (the bubble mirrors what's being spoken).
      if (!liveStore.getState().voiceReplies) return;
      liveStore.bumpChatActivity(); // streaming reply holds the overlay open
      if (liveStore.getState().awaitingReply) {
        liveStore.setState({ awaitingReply: false });
      }
      const map = assistantMsgRef.current;
      if (!map.has(responseId)) {
        map.set(
          responseId,
          liveStore.addChatMessage('assistant', text, { pending: true }),
        );
      } else {
        liveStore.updateChatMessage(map.get(responseId), text, {
          pending: true,
        });
      }
    });
    runtime.on('assistant_done', ({ responseId, text }) => {
      liveStore.bumpChatActivity();
      liveStore.setState({ awaitingReply: false, agentActing: false });
      const map = assistantMsgRef.current;
      const voiceOff = !liveStore.getState().voiceReplies;
      if (map.has(responseId)) {
        // A bubble was streamed (voice mode) — finalize it.
        liveStore.updateChatMessage(map.get(responseId), text, {
          pending: false,
        });
        map.delete(responseId);
      } else if (text && text.trim() && !(voiceOff && isTrivialAck(text))) {
        // Text mode: render the answer now, UNLESS it's a bare ack — the
        // receipt + card choreography already confirm the action.
        liveStore.addChatMessage('assistant', text);
      }
      // Wake-word listening window: clinic mode closes after each answer;
      // conversation mode keeps the mic open for natural back-and-forth.
      if (
        wakeInitiatedRef.current &&
        liveStore.getState().agentMode === 'clinic'
      ) {
        wakeInitiatedRef.current = false;
        clearTimeout(wakeListenTimerRef.current);
        setTimeout(() => {
          runtimeRef.current?.stopListening();
          blipDown();
        }, 1500);
      }
    });
    // Spoken turn committed: place a pending user bubble immediately so
    // the conversation reads in true order (transcripts lag the reply).
    runtime.on('user_speech_committed', ({ itemId }) => {
      liveStore.bumpChatActivity();
      const id = liveStore.addChatMessage('user', '…', { pending: true });
      if (itemId) speechMsgRef.current.set(itemId, id);
      liveStore.setState({ awaitingReply: true });
    });
    runtime.on('user_transcript', ({ text, itemId }) => {
      const placeholderId = itemId && speechMsgRef.current.get(itemId);
      if (placeholderId) {
        liveStore.updateChatMessage(placeholderId, text, { pending: false });
        speechMsgRef.current.delete(itemId);
      } else {
        liveStore.addChatMessage('user', text);
      }
    });
    runtime.on('error', () =>
      liveStore.setState({ awaitingReply: false, agentActing: false }),
    );
    // Every executed mutating tool posts a receipt into the chat ledger;
    // read-only tools post a faint reasoning step ("pulled Jimmy's history").
    runtime.on('tool_call', ({ name, args, result }) => {
      const receipt = receiptForToolCall(name, args, result);
      if (receipt) {
        liveStore.addReceipt(receipt);
        return;
      }
      const step = stepForToolCall(name, args, result);
      if (step) liveStore.addChatMessage('step', step);
    });

    runtime.addTools(createCoreTools({ apiClient, navigate }));
    runtime.addContext(
      'comprehend_live_panel',
      liveStore.getAgentContextSnapshot(),
    );
    runtime.setRecordingActive(liveStore.getState().anyRecording);

    await runtime.init();
    return runtime;
  }, [apiClient, navigate]);

  // "Hey Comprehend" — open a listening window on the (pre-warmed) session.
  const handleWakeWord = useCallback(async () => {
    try {
      // Flush the ambient accumulator FIRST, but transcription-ONLY (noAct):
      // the pre-wake ~15s still gets committed for context, yet the ambient
      // brain must NOT act on it — the realtime agent ("Hey Comprehend ...") is
      // about to handle this command, and double-acting was the bug.
      // ignoreSuppression because this flush's onstop lands AFTER the lines
      // below flip the store into an agent turn, which suppresses ambient —
      // without it the pre-wake window this call exists to commit is dropped.
      ambientRef.current?.flush(true, { noAct: true, ignoreSuppression: true });
      const runtime = await ensureRuntime();
      liveStore.openChatOverlay(); // conversation layer takes over (also expands)
      wakeInitiatedRef.current = true;
      playBlipUp(); // wake confirmation — always (eyes-free input ack)
      runtime.startListening();
      clearTimeout(wakeListenTimerRef.current);
      const windowMs =
        liveStore.getState().agentMode === 'conversation'
          ? CONVERSATION_LISTEN_TIMEOUT_MS
          : WAKE_LISTEN_TIMEOUT_MS;
      wakeListenTimerRef.current = setTimeout(() => {
        wakeInitiatedRef.current = false;
        runtimeRef.current?.stopListening();
        blipDown();
      }, windowMs);
    } catch (error) {
      console.error('Wake word handling failed:', error);
    }
  }, [ensureRuntime]);

  // Mic-opening arming: wake word + realtime session pre-warm. Split out of
  // the Pulse effect below because it must react to autoListen — passive
  // listening is opt-in, so NOTHING here runs until the clinician turns it on,
  // and flipping the switch arms/disarms live without a remount.
  useEffect(() => {
    if (liveStore.getState().trayMode !== 'ambient') return undefined;
    if (!canArmMic()) return undefined;
    // Wake-word key fetch can race the session/profile load on mount —
    // retry with backoff instead of giving up for the whole session.
    let armAttempts = 0;
    let armTimer = null;
    let cancelled = false;
    const tryArm = async () => {
      const armed = await armWakeWord(apiClient, handleWakeWord);
      if (cancelled) return;
      liveStore.setState({ wakeArmed: armed });
      if (!armed && armAttempts < 3) {
        armAttempts += 1;
        armTimer = setTimeout(tryArm, 15 * 1000 * armAttempts);
      }
    };
    tryArm();
    const prewarm = setTimeout(() => {
      ensureRuntime().catch((e) => console.warn('Pre-warm failed:', e));
    }, PREWARM_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(prewarm);
      clearTimeout(armTimer);
      disarmWakeWord();
      liveStore.setState({ wakeArmed: false });
      runtimeRef.current?.destroy();
      runtimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, state.trayMode, state.autoListen, state.micKilled]);

  // The Pulse (no mic involved — schedule polling + note-finish fan-out), so
  // it runs regardless of whether passive listening is switched on.
  // MultiRecord++ (V0.5) mode runs NONE of this. It's just the timeline +
  // recorder + schedule loader.
  useEffect(() => {
    if (liveStore.getState().trayMode !== 'ambient') return undefined;

    const pulse = new Pulse({
      apiClient,
      finishNote: demoRef.current
        ? demoRef.current.finishNote
        : async ({ conversationId, patientName }) => {
            // Production: pull fresh details and turn gaps into queue cards.
            try {
              const details =
                await apiClient.getConversationDetails(conversationId);
              if (!details || details.error) return;
              (details.requirement_evaluations || [])
                .filter((e) => e.status && !['fully met', 'not applicable'].includes(e.status))
                .slice(0, 3)
                .forEach((e) =>
                  liveStore.addQueueItem({
                    kind: 'nudge',
                    source: 'compliance',
                    severity: e.is_required ? 'urgent' : 'warn',
                    conversationId,
                    patientName,
                    title: e.requirement_text,
                    detail: e.status,
                    proposedText: e.suggestion || null,
                  }),
                );
              liveStore.addQueueItem({
                kind: 'review',
                source: 'compliance',
                severity: (details.score || 0) < 60 ? 'urgent' : 'info',
                conversationId,
                patientName,
                title: `${patientName} — note ready to review`,
                detail: details.score ? `Grade pending review` : 'Finished',
              });
            } catch (error) {
              console.warn('finishNote failed:', error);
            }
          },
    });
    pulseRef.current = pulse;
    pulse.start();

    return () => {
      pulse.stop();
    };
    // trayMode in deps: profile can resolve mode after mount, and switching
    // tears down ambient (cleanup) so multirecord mode runs none of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, state.trayMode]);

  // The ambient loop (docs/comprehend-live-ambient.md): owns the mic while
  // the panel is up. One backend call per VAD chunk; the brain commits
  // settled transcript server-side and raises a GATE when something needs
  // acting. We hand the gate's plain-language instruction to the SAME agent
  // the wake word uses — one actor, full tool set, no second palette.
  // Demo mode runs it too (passthrough to the real brain, listen-only since
  // demo convo ids don't resolve server-side) — easiest way to test.
  const ambientRef = useRef(null);
  const handleAmbientGate = useCallback(
    (instruction) => {
      // Light the screen aura while it works the overheard instruction —
      // the clinician sees the agent acting even with no mic turn open.
      // Cleared on assistant_done / error.
      liveStore.setState({ agentActing: true });
      (async () => {
        try {
          const runtime = await ensureRuntime();
          // Do NOT pop the conversation overlay for an overheard action:
          // the card choreography + the receipt (with undo) already evidence
          // it, and the overlay would cover the very card animation that is
          // the proof. The conversation layer is for when the agent has
          // something to SAY (a question answer surfaces via the ghost
          // trace / next reply), not when it quietly DOES something.
          // Tag the source so the agent KNOWS it's overheard (not spoken to
          // it) and can weigh that — we trust it to act, not gate it.
          runtime.sendUserText(`[Ambient — overheard just now] ${instruction}`);
        } catch (e) {
          console.warn('Ambient gate handoff failed:', e);
        }
      })();
    },
    [ensureRuntime],
  );

  useEffect(() => {
    const supported = typeof apiClient.ambientChunk === 'function';
    if (
      state.trayMode !== 'ambient' ||
      state.panelView === 'timeline' || // demoing the timeline — free the mic
      !state.expanded ||
      !supported ||
      !state.autoListen || // passive listening not switched on
      state.micKilled ||
      state.micMuted // soft mute — pause passive listening
    )
      return undefined;
    const ambient = new AmbientRuntime({
      apiClient,
      onGate: handleAmbientGate,
      onCatchup: (active) => {
        liveStore.setState({ ambientCatchingUp: active });
        // Safety net: if a forced round-trip ever hangs, don't leave the
        // bolt spinning forever.
        clearTimeout(catchupTimerRef.current);
        if (active) {
          catchupTimerRef.current = setTimeout(
            () => liveStore.setState({ ambientCatchingUp: false }),
            15000,
          );
        }
      },
      onStatus: (st, reason) => {
        liveStore.setState({
          ambientStatus: st,
          ambientPausedReason: st === 'paused' ? reason : null,
        });
        if (st === 'paused') {
          // Demo cap tripped (see TODO in ambient.js) — say why in the
          // ledger; collapsing and reopening the panel resumes.
          liveStore.addReceipt({
            text: 'Ambient listening paused',
            why:
              reason === 'cap'
                ? '1-hour session cap (demo safety limit)'
                : 'No speech for a while — assuming nobody is there',
          });
        }
      },
    });
    ambientRef.current = ambient;
    ambient.start().catch((e) => console.warn('Ambient mic unavailable:', e));
    return () => {
      ambient.stop();
      if (ambientRef.current === ambient) ambientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.trayMode,
    state.panelView,
    state.expanded,
    apiClient,
    handleAmbientGate,
    state.autoListen,
    state.micKilled,
    state.micMuted,
  ]);

  // MultiRecord++ (V0.5) recording is owned by <MultiRecordEngine/> (mounted in
  // the always-present Shell). It drives the PROVEN classic recorder
  // (api/Recorder.js) from the schedule auto-switcher — no custom recorder here.

  // Hard mic kill (header button): every mic holder goes down — ambient
  // (the effect above), the wake-word engine, AND the realtime session,
  // whose prewarmed stream otherwise keeps the browser mic indicator lit
  // while idle. Typing into chat after a kill recreates the session (and
  // the indicator) — that's the user's call, not ours.
  const prevKilledRef = useRef(false);
  useEffect(() => {
    const was = prevKilledRef.current;
    prevKilledRef.current = state.micKilled;
    if (state.micKilled && !was) {
      disarmWakeWord();
      liveStore.setState({ wakeArmed: false });
      runtimeRef.current?.destroy();
      runtimeRef.current = null;
    } else if (!state.micKilled && was && canArmMic()) {
      armWakeWord(apiClient, handleWakeWord).then((armed) =>
        liveStore.setState({ wakeArmed: armed }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.micKilled]);

  // Soft mute (chat-bar toggle): pause passive listening for a demo without
  // the hard kill's teardown. Disarm the wake word and end any in-flight
  // realtime turn, but DON'T destroy the session — so unmuting is instant and
  // doesn't re-flash the browser mic-permission churn. (The ambient effect
  // above also bails while muted.)
  const prevMutedRef = useRef(false);
  useEffect(() => {
    const was = prevMutedRef.current;
    prevMutedRef.current = state.micMuted;
    if (state.micMuted && !was) {
      wakeInitiatedRef.current = false;
      clearTimeout(wakeListenTimerRef.current);
      disarmWakeWord();
      liveStore.setState({ wakeArmed: false });
      runtimeRef.current?.stopListening();
    } else if (!state.micMuted && was && canArmMic()) {
      armWakeWord(apiClient, handleWakeWord).then((armed) =>
        liveStore.setState({ wakeArmed: armed }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.micMuted]);

  // Drill-in context enrichment: the chat "gets more interesting" about the
  // patient whose card is open. Dynamic-MCP pattern: mount/unmount context.
  useEffect(() => {
    const runtime = runtimeRef.current;
    const conversationId = state.drillInConversationId;
    if (!runtime) return undefined;
    if (!conversationId) {
      runtime.removeContext('focused_patient');
      return undefined;
    }
    const session = liveStore
      .getState()
      .sessions.find((s) => s.conversationId === conversationId);
    if (!session) return undefined;
    let cancelled = false;
    (async () => {
      let history = null;
      try {
        if (session.group) {
          history = await apiClient.getPatientHistory(session.group, 3);
        }
      } catch (e) {
        history = null;
      }
      if (!cancelled) {
        runtime.addContext('focused_patient', {
          patientName: session.patientName,
          todayStatus: session.status,
          grade: session.grade,
          history: history && !history.error ? history : null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.drillInConversationId, apiClient]);

  const actions = useMemo(
    () => ({
      toggleExpanded: () =>
        liveStore.setExpanded(!liveStore.getState().expanded),
      setExpanded: (v) => liveStore.setExpanded(v),
      // Dev-only demo toggle (gated by apiClient.isDev at the call sites). Demo
      // wrapping is decided once at mount, so flipping the flag has to reload.
      toggleDemo: () => {
        try {
          if (liveStore.getState().demo) localStorage.removeItem(DEMO_FLAG_KEY);
          else localStorage.setItem(DEMO_FLAG_KEY, '1');
        } catch (e) {
          // storage unavailable — nothing to toggle
        }
        // In the extension, location.reload() re-requests the SPA route (e.g.
        // /User/Home), which 404s on a chrome-extension:// origin — reload the
        // real entry file instead; the router redirects back.
        /* global chrome */
        if (typeof chrome !== 'undefined' && chrome.runtime?.getURL) {
          window.location.href = chrome.runtime.getURL('index.html');
        } else {
          window.location.reload();
        }
      },
      openDrillIn: (conversationId) => {
        liveStore.openDrillIn(conversationId);
        const session = liveStore
          .getState()
          .sessions.find((s) => s.conversationId === conversationId);
        if (session) focusTracker.touchPatient(session.patientName);
      },
      closeDrillIn: () => liveStore.closeDrillIn(),
      setReviewMode: (on) => liveStore.setReviewMode(on),

      sendText: async (text) => {
        if (!text?.trim()) return;
        liveStore.openChatOverlay();
        liveStore.addChatMessage('user', text.trim());
        liveStore.setState({ awaitingReply: true });
        const runtime = await ensureRuntime();
        if (runtime.status === 'ready' || runtime.status === 'listening') {
          runtime.sendUserText(text);
        } else {
          liveStore.addChatMessage(
            'assistant',
            'I could not reach the live agent — check your connection and try again.',
          );
        }
      },

      // Push-to-talk (the wake word rides the same path via handleWakeWord)
      micDown: async () => {
        liveStore.openChatOverlay();
        const runtime = await ensureRuntime();
        runtime.startListening();
      },
      micUp: () => runtimeRef.current?.stopListening(),

      toggleVoiceReplies: () => {
        const next = !liveStore.getState().voiceReplies;
        liveStore.setState({ voiceReplies: next });
        persistVoiceRepliesPref(next);
        runtimeRef.current?.setVoiceRepliesPreferred(next);
      },

      fixItem: async (item, instruction) => {
        focusTracker.touchQueueItem(item);
        return applyQueueItemFix(apiClient, item, instruction);
      },
      undoItem: (item) => undoQueueItemFix(apiClient, item),
      dismissItem: (item) => dismissQueueItemAnimated(item),
      undoReceipt: (messageId) => liveStore.undoReceipt(messageId),

      // Settings (gear in the chat bar)
      toggleSettings: () =>
        liveStore.setState({
          settingsOpen: !liveStore.getState().settingsOpen,
        }),
      setVerbosity: (level) => {
        liveStore.setState({ agentVerbosity: level });
        persistLocal(SETTINGS_KEYS.VERBOSITY_KEY, level);
        runtimeRef.current?.setVerbosity(level);
      },
      setAgentMode: (mode) => {
        liveStore.setState({ agentMode: mode });
        persistLocal(SETTINGS_KEYS.MODE_KEY, mode);
      },
      setMicId: (deviceId) => {
        liveStore.setState({ micId: deviceId });
        persistLocal(SETTINGS_KEYS.MIC_KEY, deviceId);
        runtimeRef.current?.setDevice(deviceId || null);
      },
      // Opt in/out of passive listening (persisted). The arming effect keyed
      // on state.autoListen does the actual work, so this stays a pure
      // preference write — no imperative mic handling here.
      setAutoListen: (on) => liveStore.setAutoListen(on),
      toggleAutoListen: () =>
        liveStore.setAutoListen(!liveStore.getState().autoListen),
      // Hard mic cut: forces the in-flight utterance to commit (noise kill)
      muteMic: () => {
        wakeInitiatedRef.current = false;
        clearTimeout(wakeListenTimerRef.current);
        runtimeRef.current?.stopListening();
        blipDown();
      },

      // ⚡ Catch up: flush the ambient buffer and have the brain act on
      // everything just said (the queue-to-zero moment).
      ambientCatchUp: () => ambientRef.current?.flush(true),
      toggleMicKilled: () =>
        liveStore.setMicKilled(!liveStore.getState().micKilled),
      // Soft mute toggle (chat bar): stops passive listening for demos without
      // tearing down the realtime session like the hard kill does.
      toggleMicMuted: () =>
        liveStore.setMicMuted(!liveStore.getState().micMuted),

      openChatOverlay: () => liveStore.openChatOverlay(),
      reopenChat: () => liveStore.reopenChat(),
      bumpChatActivity: () => liveStore.bumpChatActivity(),
      closeChatOverlay: () => liveStore.closeChatOverlay(),
      setChatOverlayMode: (mode) => liveStore.setChatOverlayMode(mode),

      openVisit: (conversationId) => {
        // Navigating away should close the tray so the page is visible.
        liveStore.setExpanded(false);
        navigate(`/User/conversations/details#${conversationId}`);
      },
      // Right-click → Edit patient name. Renames the patient (by group) via
      // the existing /v1/patient/edit, then re-hydrates so the timeline shows
      // the new name.
      editPatientName: async (conversationId, fullName) => {
        const session = liveStore
          .getState()
          .sessions.find(
            (s) => String(s.conversationId) === String(conversationId),
          );
        const group = session?.group;
        if (!group || !fullName?.trim()) return undefined;
        const parts = fullName.trim().split(/\s+/);
        const first = parts.shift() || '';
        const last = parts.join(' ');
        // Real write — always the real client (demo wrapper canned editPatient).
        await realApiClient.editPatient(group, 'firstName', first);
        if (last) await realApiClient.editPatient(group, 'lastName', last);
        return liveStore.hydrate(apiClient);
      },

      refresh: () => liveStore.hydrate(apiClient),

      /* ===== MultiRecord++ (V0.5) ===== */
      // Primary CTA: consent the mic once and follow the schedule clock.
      startRecordingDay: () => {
        liveStore.setAutoSwitch(true);
        liveStore.setRecordingDay(true);
      },
      stopRecordingDay: () => {
        // Explicit stop is a full reset — clear the day AND manual taps.
        liveStore.setAutoSwitch(false);
        liveStore.setRecordingDay(false);
        liveStore.clearManualRecording();
      },
      // Ambient demo toggle: flip the tray between the agent (queue+chat)
      // and the vertical timeline. Only meaningful when the ambient flag is
      // on (those users get both); multirecord-only users always see the
      // timeline.
      togglePanelView: () => liveStore.togglePanelView(),
      // Customizable record window (minutes before start / after end).
      setRecordBuffers: (beforeMin, afterMin) =>
        liveStore.setRecordBuffers(beforeMin, afterMin),
      // Auto-record on schedule (off by default — see liveStore).
      setAutoRecordingEnabled: (on) => {
        const enabled = Boolean(on);
        liveStore.setAutoSwitch(enabled);
        const manualActive =
          liveStore.getState().manualOverrides.manualOn.length > 0;
        liveStore.setRecordingDay(enabled || manualActive);
      },
      // Pin a visit to a time window {start, end} (drawn by dragging an empty
      // span, or moved by the card grip). Optimistic + local override now;
      // persisted to the backend (so it syncs web <-> phone) once the backend
      // returns the scheduled-time fields.
      setVisitWindow: (conversationId, startMs, endMs) => {
        liveStore.setVisitWindow(conversationId, startMs, endMs);
        if (!liveStore.getState().scheduledTimesSupported) return;
        (async () => {
          try {
            await realApiClient.editConversation(
              conversationId,
              'scheduledStart',
              startMs,
            );
            if (Number.isFinite(endMs) && endMs > startMs) {
              await realApiClient.editConversation(
                conversationId,
                'scheduledEnd',
                endMs,
              );
            }
          } catch (e) {
            console.warn('Persist visit window failed:', e);
          }
        })();
      },

      // Tap a visit card -> directly start/stop recording it. Turning one ON
      // also starts the day (mic consent) if it isn't already — so a tap
      // actually records, instead of silently setting an override that does
      // nothing until "Start my day".
      setVisitRecording: (conversationId, on) => {
        const s = liveStore.getState();
        // A persisted auto preference can outlive the recording-day gate
        // across reloads. If the UI is currently stopped, a card tap is an
        // explicitly manual start and must not revive that hidden preference.
        const autoEnabled = s.recordingDay && s.autoSwitch;
        const scheduleWantsIt = computeActiveSet(
          s.schedule,
          Date.now(),
          { manualOn: [], manualOff: [] },
          {
            beforeMs: (s.bufferBeforeMin || 0) * 60000,
            afterMs: (s.bufferAfterMin || 0) * 60000,
            auto: autoEnabled,
          },
        ).includes(String(conversationId));
        liveStore.setManualRecord(conversationId, on, scheduleWantsIt);
        const next = liveStore.getState();
        if (on && !next.recordingDay) {
          liveStore.setAutoSwitch(false);
          liveStore.setRecordingDay(true);
        } else if (
          !on &&
          !next.autoSwitch &&
          next.manualOverrides.manualOn.length === 0
        ) {
          liveStore.setRecordingDay(false);
        }
      },
      renameVisit: async (conversationId, name) => {
        // Real write (demo wrapper's editConversation is a canned no-op).
        // Field is 'name' — the backend rejects 'conversationName'.
        await realApiClient.editConversation(conversationId, 'name', name);
        return liveStore.hydrate(apiClient);
      },
      // Schedule loader: parse (voice/text/screenshot) -> proposed visits.
      // Always the real endpoint (the demo's canned client has no parse).
      parseSchedule: (payload) => realApiClient.parseSchedule(payload),
      // Calendar sync (CORA / WebPT). Availability is resolved from the user's
      // EMR + sync_cal flag; the loader shows the button only when available.
      syncCalContext: () => getSyncCalContext(realApiClient),
      syncScheduleCal: async (ctx) => {
        const result = await runScheduleSync(realApiClient, ctx || {});
        await liveStore.hydrate(apiClient);
        return result;
      },
      // Confirm -> for each proposed name, create an UNASSIGNED conversation
      // (no patient) and name it after the scheduled person. We deliberately
      // do NOT resolve or create a patient: the loader just stages the day's
      // visits as named conversations; the clinician assigns a real patient
      // later if they want. newConversation('') omits patientId, so the visit
      // has no patient; editConversation then sets conversationName. Any that
      // fail to create are returned as `unresolved`. Real writes -> realApiClient.
      createVisitsFromProposals: async (visits) => {
        const created = [];
        const unresolved = [];
        for (const v of visits || []) {
          const name = (v.patientName || '').trim();
          if (!name) continue;
          try {
            // Empty patient id -> conversation/new creates it with no patient.
            // eslint-disable-next-line no-await-in-loop
            const convo = await realApiClient.newConversation('', null);
            const conversationId = convo?.uid;
            if (!conversationId) {
              unresolved.push({ name, reason: 'could not create visit' });
              continue;
            }
            try {
              // Name the conversation after the person (name only — no patient
              // record is touched). Field is 'name' — the backend rejects
              // 'conversationName'. Best-effort: the visit exists regardless.
              // eslint-disable-next-line no-await-in-loop
              await realApiClient.editConversation(
                conversationId,
                'name',
                name,
              );
            } catch (e) {
              // naming is cosmetic — keep the created visit
            }
            const startMs = proposalTimeToMs(v.time);
            if (Number.isFinite(startMs)) {
              const parsedDurationMin = Number(v.durationMin);
              const durationMin =
                Number.isFinite(parsedDurationMin) && parsedDurationMin > 0
                  ? parsedDurationMin
                  : 30;
              const endMs = startMs + durationMin * 60 * 1000;
              liveStore.setVisitWindow(conversationId, startMs, endMs);
              try {
                // Best-effort server persistence. The local override above keeps
                // the UX usable on older/local backends that reject these fields.
                // eslint-disable-next-line no-await-in-loop
                await realApiClient.editConversation(
                  conversationId,
                  'scheduledStart',
                  startMs,
                );
                // eslint-disable-next-line no-await-in-loop
                await realApiClient.editConversation(
                  conversationId,
                  'scheduledEnd',
                  endMs,
                );
              } catch (e) {
                // local schedule override already applied
              }
            }
            created.push({ visit: v, conversationId, group: null });
          } catch (e) {
            unresolved.push({ name, reason: 'could not create visit' });
          }
        }
        // Hydrate rebuilds the day's schedule from conversations: scheduled
        // times first, then local overrides, then createdAt.
        if (created.length) await liveStore.hydrate(apiClient);
        return { created, unresolved };
      },
    }),
    [apiClient, realApiClient, ensureRuntime, navigate],
  );

  const value = useMemo(
    () => ({ state, actions, apiClient }),
    [state, actions, apiClient],
  );

  return (
    <ComprehendLiveContext.Provider value={value}>
      {children}
    </ComprehendLiveContext.Provider>
  );
};

export const useComprehendLive = () => {
  const ctx = useContext(ComprehendLiveContext);
  if (!ctx) {
    throw new Error(
      'useComprehendLive must be used inside ComprehendLiveProvider',
    );
  }
  return ctx;
};

export default useComprehendLive;
